/**
 * Os registros da jornada no painel "Eventos e horários" (E156.F3, ADR-0106 §2.8).
 *
 * Módulo próprio porque `QuadroPorEixosJornada` já carrega a lista posicionada e os itens do
 * eixo. Aqui ficam os instantes sem fuso declarado à parte, com o literal, e a leitura
 * registrada que a régua mediu (intervalo e origem das duas pontas); os ticks, os registros
 * recebidos e as dúvidas vivem em `QuadroPorEixosRegistrosArquivos`. APENAS o que o backend já
 * devolveu — a tela não recalcula intervalo, vínculo nem posição.
 */
import { Badge, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  InstanteDaJornadaData,
  JornadaApuradaData,
  LeituraDaJornadaData,
} from '@/lib/exame_eletronico_jornada';
import {
  instanteLocalComoConsta,
  origemDoRegistro,
  rotuloDoInstante,
} from '@/lib/exame_eletronico_jornada';

import {
  DuvidasParaAOperacao,
  LINHA,
  RegistrosRecebidos,
  Subtitulo,
  TicksDosArquivos,
} from './QuadroPorEixosRegistrosArquivos';

const tj = ptBR.exameEletronico.quadro.jornada;

/** Os instantes sem posição: o horário como consta e a fonte, nunca uma posição inventada. */
export function SemFusoDeclarado({
  instantes,
}: {
  instantes: readonly InstanteDaJornadaData[];
}): JSX.Element | null {
  if (instantes.length === 0) return null;
  return (
    <section data-testid="quadro-sem-fuso" className="space-y-sc-1">
      <Subtitulo>{tj.semFuso.titulo}</Subtitulo>
      <ul className="space-y-sc-1">
        {instantes.map((instante, indice) => (
          <li key={indice} className={LINHA} data-testid={`quadro-sem-fuso-${indice}`}>
            <MonoData variant="muted">
              {instante.registro?.instante_literal ??
                (instante.em_local ? instanteLocalComoConsta(instante.em_local) : '')}
            </MonoData>
            <span className="font-medium text-fg">
              {instante.registro
                ? instante.registro.evento_literal
                : rotuloDoInstante(instante.rotulo)}
            </span>
            <Badge variant="warning">{tj.semFuso.selo}</Badge>
            {instante.registro ? (
              <MonoData variant="muted">{origemDoRegistro(instante.registro.origem)}</MonoData>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="max-w-prose text-caption text-fg-dim">{tj.semFuso.nota}</p>
    </section>
  );
}

/** A leitura registrada: início até o aceite, os segundos (ou os limites, sem os segundos) e a origem das duas pontas. */
export function LeiturasRegistradas({
  leituras,
}: {
  leituras: readonly LeituraDaJornadaData[];
}): JSX.Element {
  return (
    <section data-testid="quadro-leituras" className="space-y-sc-1">
      <Subtitulo>{tj.leitura.titulo}</Subtitulo>
      {leituras.length === 0 ? (
        <p className="max-w-prose text-body-sm text-fg-dim">{tj.leitura.nenhuma}</p>
      ) : (
        <ul className="space-y-sc-1">
          {leituras.map((leitura, indice) => (
            <li
              key={indice}
              className={LINHA}
              data-testid={`quadro-leitura-${indice}`}
              data-apressada={leitura.apressada}
            >
              <span className="font-medium text-fg">
                {rotuloDoInstante(leitura.inicio)} {tj.leitura.ateOAceite}
              </span>
              <MonoData variant="emphasis">
                {leitura.segundos_ate == null
                  ? `${leitura.segundos} ${tj.leitura.segundos}`
                  : `${leitura.segundos}–${leitura.segundos_ate} ${tj.leitura.segundos}`}
              </MonoData>
              {leitura.segundos_ate == null ? null : (
                <span className="text-body-sm text-fg-dim">{tj.leitura.semPrecisao}</span>
              )}
              {leitura.apressada ? <Badge variant="danger">{tj.leitura.apressada}</Badge> : null}
              <MonoData variant="muted">
                {origemDoRegistro(leitura.origem_do_inicio)};{' '}
                {origemDoRegistro(leitura.origem_do_aceite)}
              </MonoData>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Tudo o que o registro do banco acrescenta ao painel; o exame anterior à Fase mostra só os sem fuso. */
export function RegistrosDaJornada({
  jornada,
  semFuso,
}: {
  jornada: JornadaApuradaData;
  semFuso: readonly InstanteDaJornadaData[];
}): JSX.Element {
  const registros = jornada.registros;
  return (
    <>
      <SemFusoDeclarado instantes={semFuso} />
      {registros ? <LeiturasRegistradas leituras={jornada.leituras ?? []} /> : null}
      <TicksDosArquivos ticks={jornada.ticks ?? []} />
      {registros ? <RegistrosRecebidos registros={registros} /> : null}
      <DuvidasParaAOperacao duvidas={jornada.registros_sem_vinculo ?? []} />
    </>
  );
}
